import { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  MenuItem,
  Chip,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  type SelectChangeEvent,
} from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { Add as AddIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useFetch, useApiCall, useApiErrorNotify } from "@/hooks";
import { membersApi, clubsApi, membershipsApi } from "@/api";
import { useNotification } from "@/context/NotificationContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import PageLoadingState from "@/components/PageLoadingState";
import { DataGridActionsCell } from "@/components/DataGridActionsCell";
import {
  DATA_GRID_INITIAL_PAGE_SIZE,
  DATA_GRID_PAGE_SIZE_OPTIONS,
} from "@/constants";
import { EMPTY_CELL_PLACEHOLDER } from "@/constants/display";
import type { Member, Club, Membership } from "@/types";

interface MemberForm {
  name: string;
  address: string;
  email: string;
  phone: string;
  since: string;
  through: string;
  clubId: string;
  membershipIds: string[];
}

const EMPTY: MemberForm = {
  name: "",
  address: "",
  email: "",
  phone: "",
  since: "",
  through: "",
  clubId: "",
  membershipIds: [],
};

export default function Members() {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const onApiError = useApiErrorNotify();
  const {
    data: members,
    loading,
    error,
    refresh,
  } = useFetch<Member[]>(membersApi.getMembers);
  const { data: clubs } = useFetch<Club[]>(clubsApi.getClubs);
  const { data: memberships } = useFetch<Membership[]>(
    membershipsApi.getMemberships,
  );

  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<MemberForm>(EMPTY);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const { execute: doCreate } = useApiCall(membersApi.createMember, {
    onError: onApiError,
  });
  const { execute: doUpdate } = useApiCall(membersApi.updateMember, {
    onError: onApiError,
  });
  const { execute: doDelete, loading: deleting } = useApiCall(
    membersApi.deleteMember,
    { onError: onApiError },
  );
  const { execute: doAssign } = useApiCall(membersApi.assignMembership, {
    onError: onApiError,
  });
  const { execute: doRemove } = useApiCall(membersApi.removeMembership, {
    onError: onApiError,
  });

  const membershipMap = useMemo(() => {
    const map = new Map<string, Membership>();
    (memberships ?? []).forEach((m) => map.set(m.id, m));
    return map;
  }, [memberships]);

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY);
    setFormOpen(true);
  };
  const openEdit = (m: Member) => {
    setEditId(m.id);
    setForm({
      name: m.name,
      address: m.address ?? "",
      email: m.email ?? "",
      phone: m.phone ?? "",
      since: m.since?.substring(0, 10) ?? "",
      through: m.through?.substring(0, 10) ?? "",
      clubId: m.clubId ?? "",
      membershipIds: (m.memberMemberships ?? []).map((mm) => mm.membershipId),
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { membershipIds, ...memberPayload } = form;
      const payload: Record<string, unknown> = { ...memberPayload };
      if (!payload.clubId) delete payload.clubId;
      if (!payload.through) delete payload.through;

      let savedMember: Member | undefined;
      if (editId) {
        savedMember = await doUpdate(editId, payload);
      } else {
        savedMember = await doCreate(payload);
      }
      if (!savedMember) return;

      const memberId = savedMember.id;
      const currentIds = new Set(
        (savedMember.memberMemberships ?? []).map((mm) => mm.membershipId),
      );
      const desiredIds = new Set(membershipIds);

      const toAdd = membershipIds.filter((id) => !currentIds.has(id));
      const toRemove = [...currentIds].filter((id) => !desiredIds.has(id));

      const activeFrom =
        form.since || new Date().toISOString().substring(0, 10);
      await Promise.all([
        ...toAdd.map((msId) => doAssign(memberId, msId, { activeFrom })),
        ...toRemove.map((msId) => doRemove(memberId, msId)),
      ]);

      notify(t("common.success"), "success");
      setFormOpen(false);
      refresh();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const ok = await doDelete(deleteId);
    if (ok !== undefined) {
      notify(t("common.success"), "success");
      setDeleteId(null);
      refresh();
    }
  };

  const setField = useCallback((field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleMembershipChange = useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const val = e.target.value;
      setForm((prev) => ({
        ...prev,
        membershipIds: typeof val === "string" ? val.split(",") : val,
      }));
    },
    [],
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: "name", headerName: t("common.name"), flex: 1, minWidth: 160 },
      { field: "email", headerName: t("common.email"), flex: 1, minWidth: 160 },
      { field: "phone", headerName: t("common.phone"), width: 130 },
      {
        field: "club",
        headerName: t("common.club"),
        width: 140,
        valueGetter: (_value: unknown, row: Member) =>
          row.club?.name ?? EMPTY_CELL_PLACEHOLDER,
      },
      {
        field: "memberships",
        headerName: t("nav.memberships"),
        flex: 1,
        minWidth: 160,
        valueGetter: (_value: unknown, row: Member) =>
          (row.memberMemberships ?? [])
            .map((mm) => mm.membership?.name)
            .filter(Boolean)
            .join(", ") || EMPTY_CELL_PLACEHOLDER,
      },
      {
        field: "since",
        headerName: t("common.since"),
        width: 110,
        valueFormatter: (v: string) => v?.substring(0, 10),
      },
      {
        field: "actions",
        headerName: t("common.actions"),
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <DataGridActionsCell
            onEdit={() => openEdit(params.row)}
            onDelete={() => setDeleteId(params.row.id)}
          />
        ),
      },
    ],
    [t],
  );

  if (loading) return <PageLoadingState />;

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
          }}
        >
          {t("nav.members")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          {t("common.create")}
        </Button>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <DataGrid
        rows={Array.isArray(members) ? members : []}
        columns={columns}
        pageSizeOptions={[...DATA_GRID_PAGE_SIZE_OPTIONS]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: DATA_GRID_INITIAL_PAGE_SIZE },
          },
        }}
        disableRowSelectionOnClick
        disableColumnResize
      />
      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editId ? t("common.edit") : t("common.create")}
        </DialogTitle>
        <DialogContent>
          <Stack
            spacing={2}
            sx={{
              mt: 1,
            }}
          >
            <TextField
              label={t("common.name")}
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              fullWidth
              required
              size="small"
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label={t("common.email")}
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                fullWidth
                size="small"
                type="email"
              />
              <TextField
                label={t("common.phone")}
                value={form.phone}
                onChange={(e) => setField("phone", e.target.value)}
                fullWidth
                size="small"
              />
            </Stack>
            <TextField
              label={t("common.address")}
              value={form.address}
              onChange={(e) => setField("address", e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label={t("common.club")}
              select
              value={form.clubId}
              onChange={(e) => setField("clubId", e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="">{EMPTY_CELL_PLACEHOLDER}</MenuItem>
              {(clubs ?? []).map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <FormControl fullWidth size="small">
              <InputLabel>{t("nav.memberships")}</InputLabel>
              <Select
                multiple
                value={form.membershipIds}
                onChange={handleMembershipChange}
                input={<OutlinedInput label={t("nav.memberships")} />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => (
                      <Chip
                        key={id}
                        label={membershipMap.get(id)?.name ?? id}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                {(memberships ?? []).map((ms) => (
                  <MenuItem key={ms.id} value={ms.id}>
                    {ms.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={2}>
              <TextField
                label={t("common.since")}
                type="date"
                value={form.since}
                onChange={(e) => setField("since", e.target.value)}
                fullWidth
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
                required
              />
              <TextField
                label={t("common.through")}
                type="date"
                value={form.through}
                onChange={(e) => setField("through", e.target.value)}
                fullWidth
                size="small"
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={!!deleteId}
        title={t("common.delete")}
        message={t("dialogs.deleteConfirm")}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
        loading={deleting}
      />
    </Box>
  );
}
